import moment from 'moment-timezone'

type DatetimeInput = string | Date | moment.Moment

export type AnalyticsPeriodParams = {
  startDatetime?: string
  endDatetime?: string
}

/**
 * 分析画面で一度に描画する期間の最大幅（月）
 * 本番のデータ負荷との兼ね合いで調整する値で、1〜12 の範囲で変える想定
 */
export const ANALYTICS_PERIOD_MAX_MONTHS = 6

const DATE_FORMAT = 'YYYY-MM-DD'

/**
 * 指定日から最大幅だけ前の日を返す
 *
 * @param date 基準日
 */
function subtractMaxPeriod(date: moment.Moment): moment.Moment {
  return date.clone().subtract(ANALYTICS_PERIOD_MAX_MONTHS, 'months')
}

/**
 * 指定日から最大幅だけ後の日を返す
 *
 * @param date 基準日
 */
function addMaxPeriod(date: moment.Moment): moment.Moment {
  return date.clone().add(ANALYTICS_PERIOD_MAX_MONTHS, 'months')
}

/**
 * 分析で選べる最初の日を返す
 *
 * @param campaignStartDatetime キャンペーン開始日時
 */
function getAnalyticsFirstDate(campaignStartDatetime: DatetimeInput): moment.Moment {
  return moment(campaignStartDatetime).startOf('day')
}

/**
 * 分析で選べる最終日を返す
 *
 * @param campaignEndDatetime キャンペーン終了日時
 */
function getAnalyticsLastDate(campaignEndDatetime: DatetimeInput): moment.Moment {
  // 終了日時が 00:00 ちょうどのキャンペーンでは、前日を最終日にするため
  return moment(campaignEndDatetime).subtract(1, 'hour').startOf('day')
}

/**
 * 開催期間が最大幅以内かを返す
 *
 * @param campaignStartDatetime キャンペーン開始日時
 * @param campaignEndDatetime キャンペーン終了日時
 */
function isCampaignWithinMaxPeriod(campaignStartDatetime: DatetimeInput, campaignEndDatetime: DatetimeInput): boolean {
  const campaignFirstDate = getAnalyticsFirstDate(campaignStartDatetime)
  const campaignLastDate = getAnalyticsLastDate(campaignEndDatetime)
  const maxLastDate = addMaxPeriod(campaignFirstDate)

  return campaignLastDate.isSameOrBefore(maxLastDate)
}

/**
 * 分析画面を開いたときの表示期間を返す
 * 開催期間が最大幅以内なら開始日〜終了日、超えるなら「今日」と「終了日」の早い方から遡って最大幅
 *
 * @param campaignStartDatetime キャンペーン開始日時
 * @param campaignEndDatetime キャンペーン終了日時
 * @param now 基準日時
 */
export function getAnalyticsDefaultPeriod(
  campaignStartDatetime: DatetimeInput,
  campaignEndDatetime: DatetimeInput,
  now: DatetimeInput = new Date()
): [Date, Date] {
  const campaignStart = moment(campaignStartDatetime)
  const campaignLastDate = getAnalyticsLastDate(campaignEndDatetime)

  if (isCampaignWithinMaxPeriod(campaignStartDatetime, campaignEndDatetime)) {
    return [campaignStart.toDate(), campaignLastDate.toDate()]
  }

  const today = moment(now).startOf('day')
  const end = moment.min(campaignLastDate, today)

  const maxStart = subtractMaxPeriod(end)
  const start = moment.max(campaignStart, maxStart)

  return [start.toDate(), end.toDate()]
}

/**
 * 分析APIで集計する期間を最大幅に丸める
 * 最大幅を超える指定は終了日から遡って最大幅にする。期間の指定がなければ、開催期間が最大幅以内なら指定なしのまま、超えるなら初期表示期間を返す
 *
 * @param params 指定された期間（YYYY-MM-DD）
 * @param campaignStartDatetime キャンペーン開始日時
 * @param campaignEndDatetime キャンペーン終了日時
 * @param now 基準日時
 */
export function clampAnalyticsPeriod(
  params: AnalyticsPeriodParams,
  campaignStartDatetime: DatetimeInput,
  campaignEndDatetime: DatetimeInput,
  now: DatetimeInput = new Date()
): AnalyticsPeriodParams {
  const hasStart = Boolean(params.startDatetime)
  const hasEnd = Boolean(params.endDatetime)

  if (hasStart && hasEnd) {
    const requestedStart = moment(params.startDatetime)
    const requestedEnd = moment(params.endDatetime)

    const maxStart = subtractMaxPeriod(requestedEnd)
    const start = moment.max(requestedStart, maxStart)

    return { startDatetime: start.format(DATE_FORMAT), endDatetime: params.endDatetime }
  }

  if (isCampaignWithinMaxPeriod(campaignStartDatetime, campaignEndDatetime)) {
    return { startDatetime: params.startDatetime, endDatetime: params.endDatetime }
  }

  const [defaultStart, defaultEnd] = getAnalyticsDefaultPeriod(campaignStartDatetime, campaignEndDatetime, now)
  const startDatetime = moment(defaultStart).format(DATE_FORMAT)
  const endDatetime = moment(defaultEnd).format(DATE_FORMAT)

  return { startDatetime, endDatetime }
}

/**
 * 期間ピッカーでその日を選べるかを返す
 * キャンペーン期間内で、かつ先に選んだ日があればそこから前後に最大幅以内の日だけを選べる
 *
 * @param date 判定する日
 * @param campaignStartDatetime キャンペーン開始日時
 * @param campaignEndDatetime キャンペーン終了日時
 * @param pickedDate 期間の片側として先に選んだ日
 */
export function isAnalyticsDateSelectable(
  date: DatetimeInput,
  campaignStartDatetime: DatetimeInput,
  campaignEndDatetime: DatetimeInput,
  pickedDate: DatetimeInput | null = null
): boolean {
  const day = moment(date).startOf('day')

  const campaignFirstDate = getAnalyticsFirstDate(campaignStartDatetime)
  const campaignLastDate = getAnalyticsLastDate(campaignEndDatetime)
  const isBeforeCampaign = day.isBefore(campaignFirstDate)
  const isAfterCampaign = day.isAfter(campaignLastDate)

  if (isBeforeCampaign || isAfterCampaign) {
    return false
  }

  if (!pickedDate) {
    return true
  }

  const picked = moment(pickedDate).startOf('day')
  const selectableFirstDate = subtractMaxPeriod(picked)
  const selectableLastDate = addMaxPeriod(picked)
  const isBeforeSelectable = day.isBefore(selectableFirstDate)
  const isAfterSelectable = day.isAfter(selectableLastDate)

  if (isBeforeSelectable || isAfterSelectable) {
    return false
  }

  return true
}
